import { WeightableEntityWithResult } from '@upright/common/model/output/scores/model-entity-data/types/types-weightable';
import styled from 'styled-components';
import { pickDefaultModelResult } from '../../../../../../../common/model/data-point-pickers';
import { computeNetImpactRatio } from '../../../../../../../common/model/output/scores/compute-net-score';
import { getBiggestImpact } from '../../../../../../../common/model/output/scores/get-biggest-impact';
import { ModelEntityWithScores } from '../../../../../../../common/model/output/scores/model-entity-data/types';
import { FinalImpactScoreType } from '../../../../../../../common/model/output/scores/types';
import { Dimension, Valence } from '../../../../../../../common/model/types';
import { friendlyShortImpactTitle } from '../../../../../../../common/utils';
import { dimensionIcon } from '../../../../../generic/components/icons/dimensions';
import { TableColumn, TableData } from '../../../../../generic/content/tables/sortable-table';
import { NumberCell, TitleCell } from '../../tables/table-styles';
import { plusIfPositive } from '../net-impact-chart/shared';
import { TopHoldingsTableType } from './types';

export function getTableData(
  itemData: Array<WeightableEntityWithResult | undefined>,
  _entity: ModelEntityWithScores | undefined,
  weightSetId: number | undefined,
  type: TopHoldingsTableType,
): TableData {
  const comparatorA = (a: WeightableEntityWithResult | undefined, b: WeightableEntityWithResult | undefined) =>
    pickScore(a, weightSetId, type) - pickScore(b, weightSetId, type);
  const comparatorB = (a: WeightableEntityWithResult | undefined, b: WeightableEntityWithResult | undefined) =>
    pickScore(b, weightSetId, type) - pickScore(a, weightSetId, type);

  const topItems = [...itemData].sort(comparatorB).slice(0, 10);

  const nameColumn: TableColumn<WeightableEntityWithResult | undefined> = {
    comparator: comparatorA,
    titleAlign: 'left',
    title: 'Holding',
    key: 'name',
    values: topItems,
    renderCell: (d, index) => {
      return <NameCell key={index}>{d?.name}</NameCell>;
    },
  };

  const strongestColumn: TableColumn<WeightableEntityWithResult | undefined> = {
    titleAlign: 'left',
    title: 'Strongest impact',
    key: 'strongest',
    values: topItems,
    renderCell: (d, index) => {
      if (!d) {
        return <TitleCell key={index}>no datum</TitleCell>;
      }
      const result = pickDefaultModelResult(d.modelResults);

      if (!result.impactScoresByTIVV) {
        return <TitleCell key={index}>no result 2 ({weightSetId})</TitleCell>;
      }
      const scores = result.impactScoresByTIVV[FinalImpactScoreType.RELATIVE];
      const biggestImpact = getBiggestImpact(scores, Valence.POSITIVE);
      const Icon = dimensionIcon(biggestImpact.impactCategory.slice(0, 1) as Dimension);
      const iconHeight = 12;
      return (
        <ImpactCell key={index}>
          <IconAndText>
            <IconWrap>
              <Icon height={iconHeight} width={iconHeight} color="#333333" />
            </IconWrap>
            <TextWrap>
              {friendlyShortImpactTitle(biggestImpact.impactCategory)} {plusIfPositive(biggestImpact.score)}
              {biggestImpact.score.toFixed(1)}
            </TextWrap>
          </IconAndText>
        </ImpactCell>
      );
    },
  };

  const netImpactRatioColumn: TableColumn<WeightableEntityWithResult | undefined> = {
    titleAlign: 'left',
    title: 'NIR*',
    key: 'net-impact-ratio',
    values: topItems,
    renderCell: (d, index) => {
      if (!d) {
        return <TitleCell key={index}>no datum</TitleCell>;
      }
      const result = pickDefaultModelResult(d.modelResults);
      if (!result.impactScoresByTIVV) {
        return <TitleCell key={index}>&nbsp;</TitleCell>;
      }
      const scores = result.impactScoresByTIVV[FinalImpactScoreType.RELATIVE];
      const netImpactRatio = computeNetImpactRatio(scores);
      return (
        <RatingCell key={index}>
          {plusIfPositive(netImpactRatio)}
          {(netImpactRatio * 100).toFixed(0)}%
        </RatingCell>
      );
    },
  };

  const weightColumn: TableColumn<WeightableEntityWithResult | undefined> = {
    titleAlign: 'right',
    title: 'Weight',
    key: 'weight',
    values: topItems,
    renderCell: (d, index) => {
      const value = d?.weight ?? 0;
      return d ? <WeightCell key={index}>{value.toFixed(1)}%</WeightCell> : <NumberCell key={index}>&nbsp;</NumberCell>;
    },
  };

  return [nameColumn, strongestColumn, netImpactRatioColumn, weightColumn];
}

function pickScore(
  item: WeightableEntityWithResult | undefined,
  _weightSetId: number | undefined,
  type: TopHoldingsTableType,
) {
  if (!item) {
    return -99999;
  }
  const result = pickDefaultModelResult(item.modelResults);
  if (!result.impactScoresByTIVV) {
    return -99999;
  }

  if (type === TopHoldingsTableType.NET_IMPACT_RATIO) {
    const score = computeNetImpactRatio(result.impactScoresByTIVV[FinalImpactScoreType.RELATIVE]);
    return score;
  }
  return item.weight || 0;
}

const IconAndText = styled.div`
  display: flex;
`;

const IconWrap = styled.span`
  width: 12px;
  height: 12px;
`;

const TextWrap = styled.div`
  margin-left: 0.5em;
`;

const nameColumnWidth = `225px`;
const impactColumnWidth = `215px`;
const ratingColumnWidth = `20px`;
const weightColumnWidth = `22px`;

const NameCell = styled(TitleCell)`
  padding-right: 0.1em !important;
  width: ${nameColumnWidth};
  max-width: ${nameColumnWidth};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ImpactCell = styled(TitleCell)`
  width: ${impactColumnWidth};
  min-width: ${impactColumnWidth};
`;
const RatingCell = styled(NumberCell)`
  width: ${ratingColumnWidth};
  white-space: nowrap;
  overflow: hidden;
`;
const WeightCell = styled(NumberCell)`
  width: ${weightColumnWidth};
  white-space: nowrap;
  overflow: hidden;
`;
